import { createContext, useContext, useId, useState, type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { Link, useInRouterContext } from 'react-router-dom'
import { ChevronDown, FileSearch, Loader2 } from 'lucide-react'

import SourceViewerDialog from '@/components/intake/SourceViewerDialog'
import {
  verifyDiscovered,
  type DatapointConfidence,
  type DiscoveredDatapoint,
  type DiscoveredKind,
  type DiscoveredSighting,
} from '@/services/discoveredDatapoints'
import { plural } from './dataReviewFormat'
import { BAND, KINDS, formatSample } from './discoveryFormat'
import {
  addBlockedReason,
  checkRefusal,
  dealHref,
  dealName,
  evidenceOf,
  listingNote,
  placeOf,
  readingDeal,
  resultDeal,
  storedSource,
  verifyDetail,
  verifyMeta,
  verifySummary,
  withheldNote,
  type DiscoveryEvidence,
  type EvidenceTarget,
  type StoredSource,
} from './discoveryEvidence'

/**
 * One found datapoint, as a person decides it.
 *
 * The value leads, the evidence and the confidence beside it, and a name, a
 * label and a kind a person may correct on the way through. The engine's guess
 * at a name is a starting point, not a decision — and a high score is only a
 * score: a new field is added by a person.
 *
 * Every number on the row opens to what it counts. The score opens to how it
 * was reached; the deal count lists the live deals, each a link to its dossier
 * or its intake record; the file count lists the files with the deals and
 * readings each holds, each stored one a link to its original; every reading
 * names the deal that stated it; and "Check against source" re-reads the stored
 * originals and says, reading by reading, whether the label and value are still
 * there. A list the server cut says how many it holds of how many. The count is
 * the server's count of deals that still exist — the tally the harvest kept
 * counted every re-parse as a deal of its own, so when the two differ the
 * server's sentence saying so is shown beside them.
 *
 * Reading the evidence decides nothing, so all of it stays open to an account
 * that may not decide the row; only Add, Not a datapoint and the fields they
 * send are off for it.
 */

/** Given by a host that holds the page inert — the review panel: it is handed
 *  every link the row follows, and answers whether the link should still go on
 *  to navigate. */
type Follow = (target: EvidenceTarget) => boolean

const FollowContext = createContext<Follow | null>(null)

/** A link that opens inside the app when there is a router to open it in, and
 *  as a plain link when there is not (the review panel renders in either).
 *  Inside a host that follows links itself, a plain click is the host's; a
 *  click for a new tab or window still opens one and leaves the host as it is. */
function EvidenceLink({ href, loanId, className, children }: {
  href: string
  loanId: string | null
  className: string
  children: ReactNode
}) {
  const routed = useInRouterContext()
  const follow = useContext(FollowContext)
  const onClick = follow
    ? (event: MouseEvent<HTMLAnchorElement>) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        if (!follow({ href, loanId })) event.preventDefault()
      }
    : undefined
  return routed
    ? <Link to={href} className={className} onClick={onClick}>{children}</Link>
    : <a href={href} className={className} onClick={onClick}>{children}</a>
}

const LINK = 'text-amber-200 underline decoration-amber-400/30 underline-offset-2 hover:text-amber-100'

function dateText(iso: string | null): string | null {
  if (!iso) return null
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? null : at.toLocaleDateString()
}

/** How many readings a row shows before "+N more" opens the rest. */
const FIRST_READINGS = 3

/**
 * One reading, as deal · file · sheet!cell · value.
 *
 * The deal comes first because two deals on one template read the same cell
 * of the same-named file: value, cell and file alone could not say which deal
 * stated which number. The value keeps the largest type on the line and never
 * truncates — a long file name gives way before the number does.
 */
function ReadingLine({ evidence, reading }: { evidence: DiscoveryEvidence; reading: DiscoveredSighting }) {
  const deal = readingDeal(evidence, reading)
  return (
    <li className="flex min-w-0 items-baseline gap-1 text-[9px] text-stone-500">
      <span className="min-w-0 truncate">
        {deal.href
          ? <EvidenceLink href={deal.href} loanId={deal.loanId} className={LINK}>{deal.name}</EvidenceLink>
          : <span className="text-stone-300">{deal.name}</span>}
        <span> · </span>
        <span>{reading.document ?? '—'}</span>
        <span> · </span>
        <span className="font-mono">{placeOf(reading)}</span>
      </span>
      <span className="shrink-0"> · </span>
      <span className="shrink-0 font-mono text-[11px] tabular-nums text-stone-100">{formatSample(reading.value)}</span>
    </li>
  )
}

/**
 * What this label would put on a deal, and where it came from.
 *
 * This block used to render at 8.5px in the dimmest grey on the panel, under
 * form fields that filled the row — so the screen read as a list of words with
 * no numbers, which is half a finding. The number a reviewer is being asked to
 * trust is now the largest thing in the row.
 *
 * Every reading names the deal that stated it, its file, its cell and its
 * value — the first three by default, the rest when "+N more" opens them.
 */
export function Evidence({
  evidence,
  open,
  onToggle,
}: {
  evidence: DiscoveryEvidence
  open: boolean
  onToggle: () => void
}) {
  const places = evidence.sightings.filter((s) => s.sheet || s.document)
  if (places.length === 0) return null
  const more = places.length - FIRST_READINGS
  const shown = open || more <= 0 ? places : places.slice(0, FIRST_READINGS)
  const kind = evidence.derived ? 'stored' : 'live'
  const total = evidence.totals.readings
  // "Every reading" only when every reading there is is on the list.
  const label = shown.length < places.length
    ? `The first ${FIRST_READINGS} ${kind} readings`
    : places.length < total ? `${places.length} of ${total} ${kind} readings` : `Every ${kind} reading`
  const note = listingNote(places.length, total, `${kind} reading`)
  const withheld = withheldNote(new Set(shown
    .map((s) => readingDeal(evidence, s))
    .filter((deal) => deal.withheld)
    .map((deal) => deal.name)).size)
  return (
    <div className="mt-1.5 space-y-1">
      <ul aria-label={label} className="space-y-0.5">
        {shown.map((s, i) => <ReadingLine key={i} evidence={evidence} reading={s} />)}
      </ul>
      {more > 0 ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="text-[9px] text-stone-500 transition-colors hover:text-amber-200"
        >
          {open ? 'Show fewer' : `+ ${plural(more, 'more reading')}`}
        </button>
      ) : null}
      {note ? <p className="text-[8.5px] text-stone-600">{note}</p> : null}
      {withheld ? <p className="text-[8.5px] leading-relaxed text-stone-600">{withheld}</p> : null}
    </div>
  )
}

/** The live deals behind the count, each a way to the deal itself. */
function DealList({ id, evidence }: { id: string; evidence: DiscoveryEvidence }) {
  const note = listingNote(evidence.deals.length, evidence.totals.deals, 'deal')
  const withheld = withheldNote(evidence.deals.filter((deal) => deal.redacted).length)
  return (
    <ul
      id={id}
      aria-label="Deals that state it"
      className="mt-1.5 space-y-0.5 rounded border border-stone-800 bg-black/25 px-2 py-1.5"
    >
      {evidence.deals.length === 0 ? (
        <li className="text-[9px] text-stone-500">No live deal states it any more.</li>
      ) : null}
      {evidence.deals.map((deal) => {
        const received = dateText(deal.receivedAt)
        return (
          <li key={deal.dealKey} className="flex min-w-0 items-baseline gap-2 text-[9.5px]">
            {deal.redacted ? (
              <span className="truncate text-stone-300">{dealName(deal)}</span>
            ) : (
              <EvidenceLink href={dealHref(deal)} loanId={deal.loanId} className={`truncate ${LINK}`}>
                {dealName(deal)}
              </EvidenceLink>
            )}
            {deal.name ? (
              <span className="shrink-0 font-mono text-[8.5px] text-stone-600">{deal.loanId ?? deal.ingestionId}</span>
            ) : null}
            {received ? <span className="shrink-0 text-[8.5px] text-stone-600">{received}</span> : null}
          </li>
        )
      })}
      {note ? <li className="text-[8.5px] text-stone-600">{note}</li> : null}
      {withheld ? <li className="text-[8.5px] leading-relaxed text-stone-600">{withheld}</li> : null}
      {evidence.derived ? (
        <li className="text-[8.5px] leading-relaxed text-stone-600">
          From the stored readings — this server does not say which of these records still exist.
        </li>
      ) : null}
    </ul>
  )
}

/** The files the live readings came from, with the deals and readings in each.
 *  A file the server names with its stored copy opens in the source viewer. */
function FileList({ id, evidence, onOpen }: {
  id: string
  evidence: DiscoveryEvidence
  onOpen: (source: StoredSource) => void
}) {
  const note = listingNote(evidence.files.length, evidence.totals.files, 'file')
  return (
    <ul
      id={id}
      aria-label="Files it was read from"
      className="mt-1.5 space-y-0.5 rounded border border-stone-800 bg-black/25 px-2 py-1.5"
    >
      {evidence.files.length === 0 ? (
        <li className="text-[9px] text-stone-500">No live reading names a file.</li>
      ) : null}
      {evidence.files.map((file, i) => {
        const source = storedSource(evidence, file)
        return (
          <li
            key={`${file.sha256 ?? ''}:${file.document}:${i}`}
            className="flex min-w-0 items-baseline justify-between gap-2 text-[9.5px]"
          >
            {source ? (
              <button
                type="button"
                onClick={() => onOpen(source)}
                title={`Open the stored ${file.document}`}
                className={`truncate text-left ${LINK}`}
              >
                {file.document}
              </button>
            ) : (
              <span className="truncate text-stone-300" title={file.document}>{file.document}</span>
            )}
            <span className="shrink-0 text-[8.5px] text-stone-500">
              {plural(file.dealCount, 'deal')} · {plural(file.sightingCount, 'reading')}
            </span>
          </li>
        )
      })}
      {note ? <li className="text-[8.5px] text-stone-600">{note}</li> : null}
    </ul>
  )
}

/**
 * Re-read the stored originals and say, reading by reading, what is there now.
 * It writes nothing, so it is offered to an account that may not decide the row.
 */
function SourceCheck({ row, evidence }: { row: DiscoveredDatapoint; evidence: DiscoveryEvidence }) {
  const check = useMutation({ mutationFn: () => verifyDiscovered(row.id) })
  const verification = check.data
  const refused = check.isError ? checkRefusal(check.error) : null
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => check.mutate()}
        disabled={check.isPending}
        title="Re-open the stored originals and read each cell again — reads only, writes nothing"
        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wide text-amber-300/80 transition-colors hover:text-amber-200 disabled:opacity-50"
      >
        {check.isPending ? <Loader2 size={10} className="animate-spin" /> : <FileSearch size={10} />}
        Check against source
      </button>
      {refused?.busy ? (
        // The server runs only so many checks at once: a wait, not a failure.
        <p role="status" className="mt-1 text-[9px] text-stone-400">{refused.message}</p>
      ) : refused ? (
        <p className="mt-1 text-[9px] text-red-300">{refused.message}</p>
      ) : null}
      {verification ? (
        <div aria-live="polite" className="mt-1">
          <p className="text-[9px] text-stone-400">{verifySummary(verification)}</p>
          {verification.results.length > 0 ? (
            <ul aria-label="What the source holds now" className="mt-1 space-y-0.5">
              {verification.results.map((result, i) => {
                const meta = verifyMeta(result.status)
                const deal = resultDeal(evidence, result)
                const detail = verifyDetail(result)
                return (
                  <li key={i} className="flex min-w-0 items-baseline gap-1.5 text-[9px]">
                    <span className={`shrink-0 rounded px-1 py-px text-[8px] uppercase tracking-wide ${meta.chip}`}>
                      {meta.label}
                    </span>
                    <span className="truncate text-stone-500">
                      {deal.name} · {result.document ?? '—'} · {placeOf(result)}
                    </span>
                    {detail ? <span className="shrink-0 font-mono text-stone-300">{detail}</span> : null}
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** The score, as a chip that opens to how it was reached — like the deal and
 *  file counts beside it. The same words stay on hover. */
export function ConfidenceChip({
  confidence,
  open,
  controls,
  onToggle,
}: {
  confidence: DatapointConfidence
  open: boolean
  /** The id of the basis this chip opens. */
  controls: string
  onToggle: () => void
}) {
  const band = BAND[confidence.band]
  const held = confidence.cap ? `\nHeld in the weak band: ${confidence.cap}` : ''
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      onClick={onToggle}
      title={`${band.note}${held}\n\n${confidence.basis.map((b) => `· ${b.detail}`).join('\n')}`}
      className={`inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 font-mono text-[9px] tabular-nums transition-colors ${band.chip} ${band.hover}`}
    >
      {Math.round(confidence.score * 100)}%
      <ChevronDown size={8} aria-hidden className={open ? 'rotate-180' : undefined} />
    </button>
  )
}

/** How the score was reached, in the server's words: the band and what it
 *  means for who decides, what holds it in the weak band if anything does,
 *  and each piece of evidence that made it. */
function ScoreBasis({ id, confidence }: { id: string; confidence: DatapointConfidence }) {
  const band = BAND[confidence.band]
  return (
    <div id={id} className="mt-1.5 space-y-0.5 rounded border border-stone-800 bg-black/25 px-2 py-1.5">
      <p className="text-[9.5px] text-stone-300">
        <span className={`font-mono tabular-nums ${band.text}`}>{Math.round(confidence.score * 100)}%</span>
        {' '}— {band.note}
      </p>
      {confidence.cap ? (
        <p className="text-[9px] leading-relaxed text-stone-400">Held in the weak band: {confidence.cap}</p>
      ) : null}
      {confidence.basis.length > 0 ? (
        <ul aria-label="How the score was reached" className="space-y-0.5">
          {confidence.basis.map((b, i) => (
            <li key={`${b.signal}-${i}`} className="text-[9px] leading-relaxed text-stone-500">{b.detail}</li>
          ))}
        </ul>
      ) : (
        <p className="text-[9px] text-stone-500">The server sent no basis for this score.</p>
      )}
    </div>
  )
}

export function DiscoveredRow({
  row,
  busy,
  disabled = false,
  selected = false,
  onSelect,
  onFollow,
  onOpenFile,
  refusal = null,
  onAccept,
  onDismiss,
}: {
  row: DiscoveredDatapoint
  busy: boolean
  /** The account may read this row but not decide it: every control that
   *  decides is off. Its evidence still opens, and its check still runs. */
  disabled?: boolean
  /** Whether a list that dismisses in bulk has this row picked. */
  selected?: boolean
  /** Given by a list that dismisses in bulk; draws the row's checkbox. */
  onSelect?: (selected: boolean) => void
  /** Given by a host that holds the page inert (the review panel), so a deal
   *  link closes it before the deal opens; returns whether the link should
   *  still navigate. Without it a link simply navigates. */
  onFollow?: (target: EvidenceTarget) => boolean
  /** Given by a host with a source viewer of its own (the review panel, whose
   *  keys stand still while its viewer is open). Without it the row opens the
   *  stored file in a viewer of its own. */
  onOpenFile?: (source: StoredSource) => void
  /** Why the server refused this row's last Add or dismissal, in its words —
   *  shown on the row, where the decision was made. */
  refusal?: string | null
  onAccept: (edits: { name: string; label: string; kind: DiscoveredKind }) => void
  onDismiss: () => void
}) {
  const [name, setName] = useState(row.proposedName)
  const [label, setLabel] = useState(row.proposedLabel)
  const [kind, setKind] = useState<DiscoveredKind>(row.proposedKind)
  const [showScore, setShowScore] = useState(false)
  const [showDeals, setShowDeals] = useState(false)
  const [showFiles, setShowFiles] = useState(false)
  const [allReadings, setAllReadings] = useState(false)
  const [viewing, setViewing] = useState<StoredSource | null>(null)
  const openFile = onOpenFile ?? setViewing
  const scoreId = useId()
  const dealsId = useId()
  const filesId = useId()
  const heldId = useId()
  const off = busy || disabled
  const evidence = evidenceOf(row)
  const deals = row.dealCount ?? 0
  // An older server's count is its stored tally, so it is not called live.
  const dealsTitle = `${evidence.derived ? 'Recorded on' : 'Stated on'} `
    + `${plural(deals, evidence.derived ? 'deal' : 'live deal')}`
    + (row.recordedDealCount != null && row.recordedDealCount !== deals
      ? ` — recorded ${row.recordedDealCount} times` : '')
    + '. Open to see which.'
  // A new field changes what every deal records, and a new word what the
  // engine reads on every deal: one deal's workbook, one file, or evidence the
  // score calls thin is not enough for either — the server refuses the same
  // rows. Saying it is not a datapoint needs no evidence, so dismissing stays open.
  const held = addBlockedReason(row, evidence)

  return (
    <li className="rounded border border-stone-800/70 px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        {onSelect ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            aria-label={`Select “${row.displayLabel}”`}
            className="mt-0.5 h-3 w-3 shrink-0 accent-amber-400"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[11px] text-stone-100">“{row.displayLabel}”</span>
            <ConfidenceChip
              confidence={row.confidence}
              open={showScore}
              controls={scoreId}
              onToggle={() => setShowScore((v) => !v)}
            />
            <button
              type="button"
              aria-expanded={showDeals}
              aria-controls={showDeals ? dealsId : undefined}
              onClick={() => setShowDeals((v) => !v)}
              title={dealsTitle}
              className="inline-flex shrink-0 items-center gap-0.5 rounded bg-sky-500/10 px-1.5 py-0.5 text-[8px] uppercase tracking-wide text-sky-400/90 transition-colors hover:bg-sky-500/20"
            >
              {plural(deals, 'deal')}
              <ChevronDown size={8} aria-hidden className={showDeals ? 'rotate-180' : undefined} />
            </button>
            <button
              type="button"
              aria-expanded={showFiles}
              aria-controls={showFiles ? filesId : undefined}
              onClick={() => setShowFiles((v) => !v)}
              title="The files it was read from, with the deals and readings in each"
              className="inline-flex shrink-0 items-center gap-0.5 rounded bg-stone-500/10 px-1.5 py-0.5 text-[8px] uppercase tracking-wide text-stone-400 transition-colors hover:bg-stone-500/20"
            >
              {plural(evidence.totals.files, 'file')}
              <ChevronDown size={8} aria-hidden className={showFiles ? 'rotate-180' : undefined} />
            </button>
            {row.fieldExists ? (
              <span
                title="The catalog already has this field — accepting only teaches the parser the word."
                className="shrink-0 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[8px] uppercase tracking-wide text-emerald-400/90"
              >
                Word only
              </span>
            ) : null}
          </div>
          {showScore ? <ScoreBasis id={scoreId} confidence={row.confidence} /> : null}
          <FollowContext.Provider value={onFollow ?? null}>
            {showDeals ? <DealList id={dealsId} evidence={evidence} /> : null}
            {showFiles ? <FileList id={filesId} evidence={evidence} onOpen={openFile} /> : null}
            {row.evidenceNote ? (
              <p className="mt-1 text-[9px] leading-relaxed text-stone-500">{row.evidenceNote}</p>
            ) : null}
            <Evidence evidence={evidence} open={allReadings} onToggle={() => setAllReadings((v) => !v)} />
          </FollowContext.Provider>
          <SourceCheck row={row} evidence={evidence} />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={off || held !== null}
              onClick={() => onAccept({ name, label, kind })}
              title={held ?? undefined}
              aria-describedby={held ? heldId : undefined}
              className="rounded bg-amber-400/15 px-2 py-1 text-[9px] uppercase tracking-wide text-amber-200 hover:bg-amber-400/25 disabled:opacity-40"
            >
              {busy ? <Loader2 size={10} className="animate-spin" /> : 'Add'}
            </button>
            <button
              type="button"
              disabled={off}
              onClick={onDismiss}
              className="rounded px-2 py-1 text-[9px] uppercase tracking-wide text-stone-500 hover:text-stone-300 disabled:opacity-40"
            >
              Not a datapoint
            </button>
          </div>
          {/* Said in words under the actions: a held button takes no focus and
              a touch shows no title, so a reason kept on hover reached nobody
              who needed it. */}
          {held ? (
            <p id={heldId} className="max-w-[17rem] text-[9px] leading-relaxed text-stone-400">
              <span className="text-stone-500">Add is held — </span>
              {held}
            </p>
          ) : null}
          {refusal ? (
            <p role="alert" className="max-w-[17rem] text-[9px] leading-relaxed text-red-300">{refusal}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.1fr)_120px] gap-2">
        <label className="block">
          <span className="text-[8px] uppercase tracking-wide text-stone-600">Field name</span>
          <input
            value={name}
            disabled={disabled}
            onChange={(e) => setName(e.target.value)}
            aria-label={`Field name for ${row.displayLabel}`}
            className="mt-0.5 w-full rounded border border-stone-800 bg-black/40 px-1.5 py-1 font-mono text-[9.5px] text-stone-200 outline-none focus:border-amber-400/40"
          />
        </label>
        <label className="block">
          <span className="text-[8px] uppercase tracking-wide text-stone-600">Shown as</span>
          <input
            value={label}
            disabled={disabled}
            onChange={(e) => setLabel(e.target.value)}
            aria-label={`Display label for ${row.displayLabel}`}
            className="mt-0.5 w-full rounded border border-stone-800 bg-black/40 px-1.5 py-1 text-[9.5px] text-stone-200 outline-none focus:border-amber-400/40"
          />
        </label>
        <label className="block">
          <span className="text-[8px] uppercase tracking-wide text-stone-600">Kind</span>
          <select
            value={kind}
            disabled={disabled}
            onChange={(e) => setKind(e.target.value as DiscoveredKind)}
            aria-label={`Field kind for ${row.displayLabel}`}
            className="mt-0.5 w-full rounded border border-stone-800 bg-black/40 px-1.5 py-1 text-[9.5px] text-stone-200 outline-none focus:border-amber-400/40"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </label>
      </div>

      {/* As the review panel opens a record's own file: the stored original,
          sha-addressed within the record that holds it, over the page. */}
      {viewing && typeof document !== 'undefined' && createPortal(
        <div role="dialog" aria-modal="true" aria-label="Source document">
          <SourceViewerDialog
            ingestionId={viewing.ingestionId}
            files={[{ filename: viewing.filename, kind: 'attachment', sha256: viewing.sha256 }]}
            initialSha={viewing.sha256}
            anchor={viewing.anchor}
            onClose={() => setViewing(null)}
          />
        </div>,
        document.body,
      )}
    </li>
  )
}
